import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
  FindAllCampaignsQueryQuery,
    QueryFindAllCampaignsArgs
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllCampaignsQuery = gql`
  query FindAllCampaignsQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $locationFilter: String
    $communityFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCampaigns(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      locationFilter: $locationFilter
      communityFilter: $communityFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
		data {
        id
        title
        description
        question
        active
        bannerUrl
        location
        community
        campaignType
        pollOptions
        campaignLink
        startDate
        endDate
        colorCode 
        views
        clickThrough
        createdAt
        adminName
        adminLevel
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

type CampaignListItem = NonNullable<
  NonNullable<FindAllCampaignsQueryQuery["findAllCampaigns"]>["data"]
>[number];

export interface WithFindAllCampaigns {
  data: CampaignListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCampaigns: boolean;
}

interface Props {
  variables: QueryFindAllCampaignsArgs;
  children: (data: WithFindAllCampaigns) => React.ReactElement | null;
}

export const FindAllCampaignsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCampaignsQueryQuery,
    QueryFindAllCampaignsArgs
  >(findAllCampaignsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const normalizeStringArray = (values?: (string | null)[] | null) =>
    values?.filter((value): value is string => typeof value === "string" && value.trim().length > 0) || [];

  const findAllCampaignsData = data?.findAllCampaigns?.data || [];
  const normalizedCampaigns: CampaignListItem[] = findAllCampaignsData
    .filter((campaign): campaign is CampaignListItem => Boolean(campaign))
    .map((campaign) => ({
      ...campaign,
      pollOptions: normalizeStringArray(campaign?.pollOptions),
      location: normalizeStringArray(campaign?.location),
      community: normalizeStringArray(campaign?.community),
    }));
  const page = data?.findAllCampaigns?.page || variables.page;
  const pageSize = data?.findAllCampaigns?.pageSize || variables.pageSize;
  const total = data?.findAllCampaigns?.total || 0;
  const totalPages = data?.findAllCampaigns?.totalPages || 0;
  const hasMoreCampaigns = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCampaigns) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllCampaigns) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCampaigns: {
            ...fetchMoreResult?.findAllCampaigns,
            data: [
              ...(prev?.findAllCampaigns?.data || []),
              ...(fetchMoreResult?.findAllCampaigns?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreCampaigns]);

  return children({
    data: normalizedCampaigns,
    loading,
    loadMore,
    hasMoreCampaigns,
    page,
    pageSize,
    total,
    totalPages,
  });
};